/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Allocation } from '../models/Allocation';
import type { ConfigurationEntry } from '../models/ConfigurationEntry';
import type { Contact } from '../models/Contact';
import type { CountryInfo } from '../models/CountryInfo';
import type { Currency } from '../models/Currency';
import type { DashboardData } from '../models/DashboardData';
import type { EmailTemplate } from '../models/EmailTemplate';
import type { EnergyCompany } from '../models/EnergyCompany';
import type { JPasswordChange } from '../models/JPasswordChange';
import type { JPasswordReset } from '../models/JPasswordReset';
import type { JVerification } from '../models/JVerification';
import type { Message } from '../models/Message';
import type { MintCard } from '../models/MintCard';
import type { Order } from '../models/Order';
import type { OrderDiscount } from '../models/OrderDiscount';
import type { Organisation1 } from '../models/Organisation1';
import type { OwnerDisbursement } from '../models/OwnerDisbursement';
import type { PageableCollectionAllocation } from '../models/PageableCollectionAllocation';
import type { PageableCollectionCurrency } from '../models/PageableCollectionCurrency';
import type { PageableCollectionEnergyCompany } from '../models/PageableCollectionEnergyCompany';
import type { PageableCollectionOrder } from '../models/PageableCollectionOrder';
import type { PageableCollectionOrderDiscount } from '../models/PageableCollectionOrderDiscount';
import type { PageableCollectionOrderPayment } from '../models/PageableCollectionOrderPayment';
import type { PageableCollectionOrganisation } from '../models/PageableCollectionOrganisation';
import type { PageableCollectionOwnerDisbursement } from '../models/PageableCollectionOwnerDisbursement';
import type { PageableCollectionPaymentIntent } from '../models/PageableCollectionPaymentIntent';
import type { PageableCollectionPowerGenerationPricing } from '../models/PageableCollectionPowerGenerationPricing';
import type { PageableCollectionPowerGenerationProduct } from '../models/PageableCollectionPowerGenerationProduct';
import type { PageableCollectionPowerGenerationSite } from '../models/PageableCollectionPowerGenerationSite';
import type { PageableCollectionPowerGenerationSliceJson } from '../models/PageableCollectionPowerGenerationSliceJson';
import type { PageableCollectionPowerMonitor } from '../models/PageableCollectionPowerMonitor';
import type { PageableCollectionPowerPurchaseAgreement } from '../models/PageableCollectionPowerPurchaseAgreement';
import type { PageableCollectionPowerPurchasePricing } from '../models/PageableCollectionPowerPurchasePricing';
import type { PageableCollectionProcessInstance } from '../models/PageableCollectionProcessInstance';
import type { PageableCollectionTermOrCondition } from '../models/PageableCollectionTermOrCondition';
import type { PageableCollectionTotal } from '../models/PageableCollectionTotal';
import type { PageableCollectionTrackerEvent } from '../models/PageableCollectionTrackerEvent';
import type { PageableCollectionUser } from '../models/PageableCollectionUser';
import type { PostPaymentRequest } from '../models/PostPaymentRequest';
import type { PowerGenerationInfo } from '../models/PowerGenerationInfo';
import type { PowerGenerationPricing } from '../models/PowerGenerationPricing';
import type { PowerGenerationProduct } from '../models/PowerGenerationProduct';
import type { PowerGenerationSite } from '../models/PowerGenerationSite';
import type { PowerGenerationSlice } from '../models/PowerGenerationSlice';
import type { PowerMonitor } from '../models/PowerMonitor';
import type { PowerPurchaseAgreement } from '../models/PowerPurchaseAgreement';
import type { PowerPurchasePricing } from '../models/PowerPurchasePricing';
import type { PreparePaymentResponse } from '../models/PreparePaymentResponse';
import type { Process } from '../models/Process';
import type { ProcessNode } from '../models/ProcessNode';
import type { ProcessNodeInstance1 } from '../models/ProcessNodeInstance1';
import type { ProcessState } from '../models/ProcessState';
import type { ReferenceData } from '../models/ReferenceData';
import type { ResourceFile } from '../models/ResourceFile';
import type { SquarespaceLeadDto } from '../models/SquarespaceLeadDto';
import type { StripeCreateIntentResponse } from '../models/StripeCreateIntentResponse';
import type { TermOrCondition } from '../models/TermOrCondition';
import type { UploadExcelRequest } from '../models/UploadExcelRequest';
import type { User1 } from '../models/User1';
import type { UUID } from '../models/UUID';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class DefaultService {

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiTrackerHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/tracker/health',
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiTrackerMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/tracker/metrics',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiTrackerRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/tracker/refresh',
        });
    }

    /**
     * @param organisation
     * @param personal
     * @returns PageableCollectionTrackerEvent OK
     * @throws ApiError
     */
    public static trackerEventsGet(
        organisation?: Array<string>,
        personal?: boolean,
    ): CancelablePromise<PageableCollectionTrackerEvent> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/tracker/events',
            query: {
                'organisation': organisation,
                'personal': personal,
            },
        });
    }

    /**
     * @param uuid
     * @returns PowerGenerationSite OK
     * @throws ApiError
     */
    public static siteGet(
        uuid: UUID,
    ): CancelablePromise<PowerGenerationSite> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/sites/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns PowerGenerationSite OK
     * @throws ApiError
     */
    public static siteUpdate(
        uuid: UUID,
        requestBody?: PowerGenerationSite,
    ): CancelablePromise<PowerGenerationSite> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/generation/sites/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static siteDelete(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/generation/sites/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns PageableCollectionPowerGenerationSite OK
     * @throws ApiError
     */
    public static siteSearch(): CancelablePromise<PageableCollectionPowerGenerationSite> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/sites',
        });
    }

    /**
     * @param requestBody
     * @returns PowerGenerationSite OK
     * @throws ApiError
     */
    public static siteCreate(
        requestBody?: PowerGenerationSite,
    ): CancelablePromise<PowerGenerationSite> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/generation/sites',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns PowerGenerationSlice OK
     * @throws ApiError
     */
    public static sliceGet(
        uuid: UUID,
    ): CancelablePromise<PowerGenerationSlice> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/slices/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns PowerGenerationSlice OK
     * @throws ApiError
     */
    public static sliceUpdate(
        uuid: UUID,
        requestBody?: PowerGenerationSlice,
    ): CancelablePromise<PowerGenerationSlice> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/generation/slices/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static sliceDelete(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/generation/slices/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiGenerationMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/metrics',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiGenerationRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/generation/refresh',
        });
    }

    /**
     * @param organisation
     * @param site
     * @param user
     * @returns PageableCollectionPowerGenerationSliceJson OK
     * @throws ApiError
     */
    public static sliceSearch(
        organisation?: UUID,
        site?: UUID,
        user?: UUID,
    ): CancelablePromise<PageableCollectionPowerGenerationSliceJson> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/slices',
            query: {
                'organisation': organisation,
                'site': site,
                'user': user,
            },
        });
    }

    /**
     * @param requestBody
     * @returns PowerGenerationSlice OK
     * @throws ApiError
     */
    public static sliceCreate(
        requestBody?: PowerGenerationSlice,
    ): CancelablePromise<PowerGenerationSlice> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/generation/slices',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiGenerationHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/health',
        });
    }

    /**
     * @returns PowerGenerationInfo OK
     * @throws ApiError
     */
    public static powerGenerationInfo(): CancelablePromise<PowerGenerationInfo> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/slices/summary',
        });
    }

    /**
     * @param siteUuid
     * @returns PageableCollectionPowerMonitor OK
     * @throws ApiError
     */
    public static monitorSearch(
        siteUuid: UUID,
    ): CancelablePromise<PageableCollectionPowerMonitor> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/sites/{siteUuid}/monitors',
            path: {
                'siteUuid': siteUuid,
            },
        });
    }

    /**
     * @param siteUuid
     * @param requestBody
     * @returns PowerMonitor OK
     * @throws ApiError
     */
    public static monitorCreate(
        siteUuid: UUID,
        requestBody?: PowerMonitor,
    ): CancelablePromise<PowerMonitor> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/generation/sites/{siteUuid}/monitors',
            path: {
                'siteUuid': siteUuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param siteUuid
     * @param uuid
     * @returns PowerMonitor OK
     * @throws ApiError
     */
    public static monitorGet(
        siteUuid: UUID,
        uuid: UUID,
    ): CancelablePromise<PowerMonitor> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/generation/sites/{siteUuid}/monitors/{uuid}',
            path: {
                'siteUuid': siteUuid,
                'uuid': uuid,
            },
        });
    }

    /**
     * @param siteUuid
     * @param uuid
     * @param requestBody
     * @returns PowerMonitor OK
     * @throws ApiError
     */
    public static monitorUpdate(
        siteUuid: UUID,
        uuid: UUID,
        requestBody?: PowerMonitor,
    ): CancelablePromise<PowerMonitor> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/generation/sites/{siteUuid}/monitors/{uuid}',
            path: {
                'siteUuid': siteUuid,
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param siteUuid
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static monitorDelete(
        siteUuid: UUID,
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/generation/sites/{siteUuid}/monitors/{uuid}',
            path: {
                'siteUuid': siteUuid,
                'uuid': uuid,
            },
        });
    }

    /**
     * @param id
     * @returns ProcessNodeInstance1 OK
     * @throws ApiError
     */
    public static getApiProcessNodeInstances(
        id: UUID,
    ): CancelablePromise<ProcessNodeInstance1> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/process/nodeInstances/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id
     * @returns ProcessNodeInstance1 OK
     * @throws ApiError
     */
    public static putApiProcessNodeInstances(
        id: UUID,
    ): CancelablePromise<ProcessNodeInstance1> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/process/nodeInstances/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param state
     * @returns PageableCollectionProcessInstance OK
     * @throws ApiError
     */
    public static getApiProcessInstance(
        state?: Array<ProcessState>,
    ): CancelablePromise<PageableCollectionProcessInstance> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/process/instance',
            query: {
                'state': state,
            },
        });
    }

    /**
     * @param id
     * @returns Process OK
     * @throws ApiError
     */
    public static getApiProcess(
        id: UUID,
    ): CancelablePromise<Process> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/process/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiProcessRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/process/refresh',
        });
    }

    /**
     * @param eventKey
     * @param nodeName
     * @param processName
     * @param route
     * @returns any Created
     * @throws ApiError
     */
    public static postApiProcessSendEvent(
        eventKey?: string,
        nodeName?: string,
        processName?: string,
        route?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/process/sendEvent',
            query: {
                'eventKey': eventKey,
                'nodeName': nodeName,
                'processName': processName,
                'route': route,
            },
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiProcessMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/process/metrics',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static postApiProcessNewOrder(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/process/newOrder',
        });
    }

    /**
     * @param id
     * @returns ProcessNode OK
     * @throws ApiError
     */
    public static getApiProcessNodes(
        id: UUID,
    ): CancelablePromise<ProcessNode> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/process/nodes/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiProcessHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/process/health',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static postApiProcessPanelAllocation(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/process/panelAllocation',
        });
    }

    /**
     * @param email
     * @returns Message OK
     * @throws ApiError
     */
    public static verificationCode(
        email?: string,
    ): CancelablePromise<Message> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/profile/action/verification-code',
            query: {
                'email': email,
            },
        });
    }

    /**
     * @param requestBody
     * @returns any Created
     * @throws ApiError
     */
    public static registerContact(
        requestBody?: Contact,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/contact',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiUsersRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/refresh',
        });
    }

    /**
     * @param uuid
     * @returns Organisation1 OK
     * @throws ApiError
     */
    public static getOrganisation(
        uuid: UUID,
    ): CancelablePromise<Organisation1> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/organisations/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns Organisation1 OK
     * @throws ApiError
     */
    public static updateOrganisation(
        uuid: UUID,
        requestBody?: Organisation1,
    ): CancelablePromise<Organisation1> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/users/organisations/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static deleteOrganisation(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/users/organisations/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns User1 OK
     * @throws ApiError
     */
    public static getUser(
        uuid: UUID,
    ): CancelablePromise<User1> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/users/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns User1 OK
     * @throws ApiError
     */
    public static updateUser(
        uuid: UUID,
        requestBody?: User1,
    ): CancelablePromise<User1> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/users/users/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static deleteUser(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/users/users/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns User1 OK
     * @throws ApiError
     */
    public static runAsGet(): CancelablePromise<User1> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/runas',
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public static runAsRemove(): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/users/runas',
        });
    }

    /**
     * @param uuid
     * @returns TermOrCondition OK
     * @throws ApiError
     */
    public static termsAndConditionsAccept(
        uuid: UUID,
    ): CancelablePromise<TermOrCondition> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/terms-and-conditions/{uuid}/accept',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param requestBody
     * @returns Message OK
     * @throws ApiError
     */
    public static resetPasswordComplete(
        requestBody?: JPasswordReset,
    ): CancelablePromise<Message> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/profile/action/reset-password-complete',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static squarespaceLoanCalculatorWebhook(
        requestBody?: SquarespaceLeadDto,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/v1/webhooks/squarespace-loan-calculator',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static optionsApiUsersV1WebhooksSquarespaceLoanCalculator(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'OPTIONS',
            url: '/api/users/v1/webhooks/squarespace-loan-calculator',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static notifictions(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/notifications',
        });
    }

    /**
     * @returns PageableCollectionOrganisation OK
     * @throws ApiError
     */
    public static searchForAllOrganisations(): CancelablePromise<PageableCollectionOrganisation> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/organisations/all',
        });
    }

    /**
     * @param requestBody
     * @returns Message OK
     * @throws ApiError
     */
    public static verificationCodeComplete(
        requestBody?: JVerification,
    ): CancelablePromise<Message> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/profile/action/verification-code-complete',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any Created
     * @throws ApiError
     */
    public static runAs(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/runas/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns TermOrCondition OK
     * @throws ApiError
     */
    public static getTermOrCondition(
        uuid: UUID,
    ): CancelablePromise<TermOrCondition> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/terms-and-conditions/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns TermOrCondition OK
     * @throws ApiError
     */
    public static updateTermOrCondition(
        uuid: UUID,
    ): CancelablePromise<TermOrCondition> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/users/terms-and-conditions/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static deleteTermOrCondition(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/users/terms-and-conditions/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns PageableCollectionTermOrCondition OK
     * @throws ApiError
     */
    public static searchForTermOrConditions(): CancelablePromise<PageableCollectionTermOrCondition> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/terms-and-conditions',
        });
    }

    /**
     * @param requestBody
     * @returns PageableCollectionTermOrCondition OK
     * @throws ApiError
     */
    public static createTermAndConditions(
        requestBody?: Array<TermOrCondition>,
    ): CancelablePromise<PageableCollectionTermOrCondition> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/users/terms-and-conditions',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param requestBody
     * @returns TermOrCondition OK
     * @throws ApiError
     */
    public static createTermOrCondition(
        requestBody?: TermOrCondition,
    ): CancelablePromise<TermOrCondition> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/terms-and-conditions',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getToken(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/token',
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiUsersMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/metrics',
        });
    }

    /**
     * @returns PageableCollectionUser OK
     * @throws ApiError
     */
    public static searchForUsers(): CancelablePromise<PageableCollectionUser> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/users',
        });
    }

    /**
     * @param requestBody
     * @returns User1 OK
     * @throws ApiError
     */
    public static createUser(
        requestBody?: User1,
    ): CancelablePromise<User1> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/users',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param email
     * @returns Message OK
     * @throws ApiError
     */
    public static resetPassword(
        email?: string,
    ): CancelablePromise<Message> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/profile/action/reset-password',
            query: {
                'email': email,
            },
        });
    }

    /**
     * @param requestBody
     * @returns Message OK
     * @throws ApiError
     */
    public static changePassword(
        requestBody?: JPasswordChange,
    ): CancelablePromise<Message> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/profile/action/change-password',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param formType
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static squarespaceEnquiryWebhook(
        formType: string,
        requestBody?: SquarespaceLeadDto,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/v1/webhooks/squarespace/enquiry/{formType}',
            path: {
                'formType': formType,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static optionsApiUsersV1WebhooksSquarespaceEnquiry(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'OPTIONS',
            url: '/api/users/v1/webhooks/squarespace/enquiry/{formType}',
        });
    }

    /**
     * @returns PageableCollectionOrganisation OK
     * @throws ApiError
     */
    public static searchForUserOrganisations(): CancelablePromise<PageableCollectionOrganisation> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/organisations',
        });
    }

    /**
     * @param requestBody
     * @returns Organisation1 OK
     * @throws ApiError
     */
    public static createOrganisation(
        requestBody?: Organisation1,
    ): CancelablePromise<Organisation1> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/organisations',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns User1 OK
     * @throws ApiError
     */
    public static getProfile(): CancelablePromise<User1> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/profile',
        });
    }

    /**
     * @param requestBody
     * @returns User1 OK
     * @throws ApiError
     */
    public static updateProfile(
        requestBody?: User1,
    ): CancelablePromise<User1> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/users/profile',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PageableCollectionEnergyCompany OK
     * @throws ApiError
     */
    public static getActiveEnergyCompanies(): CancelablePromise<PageableCollectionEnergyCompany> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/allocations/energycompanies',
        });
    }

    /**
     * @returns PageableCollectionAllocation OK
     * @throws ApiError
     */
    public static getPersonalAllocations(): CancelablePromise<PageableCollectionAllocation> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/allocations/personal',
        });
    }

    /**
     * @param requestBody
     * @returns PageableCollectionAllocation OK
     * @throws ApiError
     */
    public static updatePersonalAllocations(
        requestBody?: Array<Allocation>,
    ): CancelablePromise<PageableCollectionAllocation> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/users/allocations/personal',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PageableCollectionTermOrCondition OK
     * @throws ApiError
     */
    public static termsAndConditionsGetAcive(): CancelablePromise<PageableCollectionTermOrCondition> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/terms-and-conditions/active',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiUsersHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/users/health',
        });
    }

    /**
     * @param uuid
     * @returns TermOrCondition OK
     * @throws ApiError
     */
    public static termsAndConditionsReject(
        uuid: UUID,
    ): CancelablePromise<TermOrCondition> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/users/terms-and-conditions/{uuid}/reject',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiConfigRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/config/refresh',
        });
    }

    /**
     * @param password
     * @returns string OK
     * @throws ApiError
     */
    public static configurationBulkGet(
        password?: string,
    ): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/config/bulk',
            query: {
                'password': password,
            },
        });
    }

    /**
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static configurationBulkUpdate(
        requestBody?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/config/bulk',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiConfigMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/config/metrics',
        });
    }

    /**
     * @param referer
     * @returns any OK
     * @throws ApiError
     */
    public static environmentGet(
        referer?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/config/env',
            headers: {
                'referer': referer,
            },
        });
    }

    /**
     * @param path
     * @param password
     * @returns ConfigurationEntry OK
     * @throws ApiError
     */
    public static configurationGet(
        path: string,
        password?: string,
    ): CancelablePromise<Array<ConfigurationEntry>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/config/entry/{path}',
            path: {
                'path': path,
            },
            query: {
                'password': password,
            },
        });
    }

    /**
     * @param path
     * @returns any OK
     * @throws ApiError
     */
    public static configurationEntryDelete(
        path: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/config/entry/{path}',
            path: {
                'path': path,
            },
        });
    }

    /**
     * @param path
     * @param encrypt
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static configurationEntryUpdate(
        path: string,
        encrypt?: boolean,
        requestBody?: ConfigurationEntry,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/config/entry/{path}',
            path: {
                'path': path,
            },
            query: {
                'encrypt': encrypt,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiConfigHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/config/health',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiDashboardHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/dashboard/health',
        });
    }

    /**
     * @param month
     * @param year
     * @param organisation
     * @returns DashboardData OK
     * @throws ApiError
     */
    public static datsetsGet(
        month: number,
        year: number,
        organisation?: UUID,
    ): CancelablePromise<DashboardData> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/dashboard/datasets/{year}/{month}',
            path: {
                'month': month,
                'year': year,
            },
            query: {
                'organisation': organisation,
            },
        });
    }

    /**
     * @param organisation
     * @returns PageableCollectionTotal OK
     * @throws ApiError
     */
    public static totalsGet(
        organisation?: UUID,
    ): CancelablePromise<PageableCollectionTotal> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/dashboard/totals',
            query: {
                'organisation': organisation,
            },
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiDashboardRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/dashboard/refresh',
        });
    }

    /**
     * @param month
     * @param year
     * @param organisation
     * @returns PageableCollectionTotal OK
     * @throws ApiError
     */
    public static monthTotalsGet(
        month: number,
        year: number,
        organisation?: UUID,
    ): CancelablePromise<PageableCollectionTotal> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/dashboard/totals/{year}/{month}',
            path: {
                'month': month,
                'year': year,
            },
            query: {
                'organisation': organisation,
            },
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiDashboardMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/dashboard/metrics',
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiMonitorMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/monitor/metrics',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiMonitorHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/monitor/health',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static postApiMonitor(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/monitor/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static postApiMonitorDuplicate(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/monitor/{uuid}/duplicate',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiMonitorRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/monitor/refresh',
        });
    }

    /**
     * @returns PageableCollectionPowerGenerationProduct OK
     * @throws ApiError
     */
    public static productCatalogue(): CancelablePromise<PageableCollectionPowerGenerationProduct> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/products/catalogue',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiOrdersHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/health',
        });
    }

    /**
     * @returns PageableCollectionPowerGenerationProduct OK
     * @throws ApiError
     */
    public static productSearch(): CancelablePromise<PageableCollectionPowerGenerationProduct> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/products',
        });
    }

    /**
     * @param requestBody
     * @returns PowerGenerationProduct OK
     * @throws ApiError
     */
    public static productCreate(
        requestBody?: PowerGenerationProduct,
    ): CancelablePromise<PowerGenerationProduct> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/products',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static postPaymentStripe(
        uuid: UUID,
        requestBody?: PostPaymentRequest,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/new/{uuid}/action/post-payment/stripe',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param stripeSignature
     * @param requestBody
     * @returns string OK
     * @throws ApiError
     */
    public static stripePaymentWebhook(
        stripeSignature?: string,
        requestBody?: Blob,
    ): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/action/payment/webhook/stripe',
            headers: {
                'stripe-signature': stripeSignature,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @param panels
     * @returns any OK
     * @throws ApiError
     */
    public static newOrderGet(
        uuid: UUID,
        panels?: number,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/new/{uuid}',
            path: {
                'uuid': uuid,
            },
            query: {
                'panels': panels,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static newOrderUpsert(
        uuid: UUID,
        requestBody?: Order,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/orders/new/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns Order OK
     * @throws ApiError
     */
    public static orderGet(
        uuid: UUID,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/orders/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns Order OK
     * @throws ApiError
     */
    public static orderUpdate(
        uuid: UUID,
        requestBody?: Order,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/orders/orders/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static orderDelete(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/orders/orders/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns Order OK
     * @throws ApiError
     */
    public static newOrderMintPayment(
        uuid: UUID,
        requestBody?: MintCard,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/new/{uuid}/action/payment/mint',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param productUuid
     * @param uuid
     * @returns PowerGenerationPricing OK
     * @throws ApiError
     */
    public static productPricingGet(
        productUuid: UUID,
        uuid: UUID,
    ): CancelablePromise<PowerGenerationPricing> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/products/{productUuid}/pricing/{uuid}',
            path: {
                'productUuid': productUuid,
                'uuid': uuid,
            },
        });
    }

    /**
     * @param productUuid
     * @param uuid
     * @returns PowerGenerationPricing OK
     * @throws ApiError
     */
    public static productPricingUpdate(
        productUuid: UUID,
        uuid: UUID,
    ): CancelablePromise<PowerGenerationPricing> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/orders/products/{productUuid}/pricing/{uuid}',
            path: {
                'productUuid': productUuid,
                'uuid': uuid,
            },
        });
    }

    /**
     * @param productUuid
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static productPricingDelete(
        productUuid: UUID,
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/orders/products/{productUuid}/pricing/{uuid}',
            path: {
                'productUuid': productUuid,
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiOrdersPaymentsPaypalClientToken(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/payments/paypal/client-token',
        });
    }

    /**
     * @param requestUuid
     * @param token
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static payManualPaymentMint(
        requestUuid: string,
        token?: string,
        requestBody?: MintCard,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payment/manual/{requestUuid}/mint/pay',
            path: {
                'requestUuid': requestUuid,
            },
            query: {
                'token': token,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns StripeCreateIntentResponse OK
     * @throws ApiError
     */
    public static newOrderCreateStripePaymentIntent(
        uuid: UUID,
    ): CancelablePromise<StripeCreateIntentResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/new/{uuid}/action/payment/stripe/create',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param requestUuid
     * @param token
     * @returns any OK
     * @throws ApiError
     */
    public static createManualPaymentPaypalOrder(
        requestUuid: string,
        token?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payment/manual/{requestUuid}/paypal/create',
            path: {
                'requestUuid': requestUuid,
            },
            query: {
                'token': token,
            },
        });
    }

    /**
     * @param productUuid
     * @returns PageableCollectionPowerGenerationPricing OK
     * @throws ApiError
     */
    public static productPricingSearch(
        productUuid: UUID,
    ): CancelablePromise<PageableCollectionPowerGenerationPricing> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/products/{productUuid}/pricing',
            path: {
                'productUuid': productUuid,
            },
        });
    }

    /**
     * @param productUuid
     * @param requestBody
     * @returns PowerGenerationPricing OK
     * @throws ApiError
     */
    public static productPricingCreate(
        productUuid: UUID,
        requestBody?: PowerGenerationPricing,
    ): CancelablePromise<PowerGenerationPricing> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/products/{productUuid}/pricing',
            path: {
                'productUuid': productUuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param requestUuid
     * @returns any OK
     * @throws ApiError
     */
    public static getManualPaymentRequestByUuid(
        requestUuid: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/payment/manual/{requestUuid}',
            path: {
                'requestUuid': requestUuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns Order OK
     * @throws ApiError
     */
    public static recoverOrder(
        uuid: UUID,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/orders/{uuid}/recovery',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns PageableCollectionOrder OK
     * @throws ApiError
     */
    public static myOrderSearch(): CancelablePromise<PageableCollectionOrder> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/myorders',
        });
    }

    /**
     * @param id
     * @returns any OK
     * @throws ApiError
     */
    public static getMyOrderInvoice(
        id: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/myinvoices/{id}/download',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param requestUuid
     * @param paymentIntentId
     * @param token
     * @returns any OK
     * @throws ApiError
     */
    public static completeManualPaymentStripeIntent(
        requestUuid: string,
        paymentIntentId?: string,
        token?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payment/manual/{requestUuid}/stripe/complete',
            path: {
                'requestUuid': requestUuid,
            },
            query: {
                'paymentIntentId': paymentIntentId,
                'token': token,
            },
        });
    }

    /**
     * @param requestUuid
     * @param token
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static createManualPaymentStripeIntent(
        requestUuid: string,
        token?: string,
        requestBody?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payment/manual/{requestUuid}/stripe/create',
            path: {
                'requestUuid': requestUuid,
            },
            query: {
                'token': token,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param paypalCertUrl
     * @param paypalTransmissionId
     * @param paypalTransmissionSig
     * @param paypalTransmissionTime
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static paypalWebhook(
        paypalCertUrl?: string,
        paypalTransmissionId?: string,
        paypalTransmissionSig?: string,
        paypalTransmissionTime?: string,
        requestBody?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payments/paypal/webhook',
            headers: {
                'paypal-cert-url': paypalCertUrl,
                'paypal-transmission-id': paypalTransmissionId,
                'paypal-transmission-sig': paypalTransmissionSig,
                'paypal-transmission-time': paypalTransmissionTime,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param requestUuid
     * @param token
     * @returns any OK
     * @throws ApiError
     */
    public static createManualPaymentCheckoutSession(
        requestUuid: string,
        token?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payment/manual/{requestUuid}/checkout-session',
            path: {
                'requestUuid': requestUuid,
            },
            query: {
                'token': token,
            },
        });
    }

    /**
     * @param uuid
     * @returns PowerGenerationProduct OK
     * @throws ApiError
     */
    public static productGet(
        uuid: UUID,
    ): CancelablePromise<PowerGenerationProduct> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/products/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns PowerGenerationProduct OK
     * @throws ApiError
     */
    public static productUpdate(
        uuid: UUID,
        requestBody?: PowerGenerationProduct,
    ): CancelablePromise<PowerGenerationProduct> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/orders/products/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns void
     * @throws ApiError
     */
    public static productDelete(
        uuid: UUID,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/orders/products/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns OrderDiscount OK
     * @throws ApiError
     */
    public static orderDiscountGet(
        uuid: UUID,
    ): CancelablePromise<OrderDiscount> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/orderdiscounts/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns OrderDiscount OK
     * @throws ApiError
     */
    public static orderDiscountUpdate(
        uuid: UUID,
        requestBody?: OrderDiscount,
    ): CancelablePromise<OrderDiscount> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/orders/orderdiscounts/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static orderDiscountDelete(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/orders/orderdiscounts/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param referer
     * @returns PreparePaymentResponse OK
     * @throws ApiError
     */
    public static newOrderCreatePaypalPayment(
        uuid: UUID,
        referer?: string,
    ): CancelablePromise<PreparePaymentResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/new/{uuid}/action/payment/paypal/create',
            path: {
                'uuid': uuid,
            },
            headers: {
                'referer': referer,
            },
        });
    }

    /**
     * @returns PageableCollectionOrder OK
     * @throws ApiError
     */
    public static existingOrderSearch(): CancelablePromise<PageableCollectionOrder> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/existing',
        });
    }

    /**
     * @param orderNumber
     * @param organisationUuid
     * @param userUuid
     * @returns PageableCollectionOrder OK
     * @throws ApiError
     */
    public static orderSearch(
        orderNumber?: string,
        organisationUuid?: UUID,
        userUuid?: UUID,
    ): CancelablePromise<PageableCollectionOrder> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/orders',
            query: {
                'orderNumber': orderNumber,
                'organisationUuid': organisationUuid,
                'userUuid': userUuid,
            },
        });
    }

    /**
     * @param requestBody
     * @returns Order OK
     * @throws ApiError
     */
    public static orderCreate(
        requestBody?: Order,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/orders',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @param paymentIntentId
     * @returns any OK
     * @throws ApiError
     */
    public static postPaymentStripeNoAuthQuery(
        uuid: UUID,
        paymentIntentId?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/new/{uuid}/action/post-payment/stripe/noauth',
            path: {
                'uuid': uuid,
            },
            query: {
                'paymentIntentId': paymentIntentId,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static postPaymentStripeNoAuth(
        uuid: UUID,
        requestBody?: PostPaymentRequest,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/new/{uuid}/action/post-payment/stripe/noauth',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param sessionId
     * @returns any OK
     * @throws ApiError
     */
    public static confirmManualPaymentCheckoutSession(
        sessionId?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payment/manual/confirm-session',
            query: {
                'session_id': sessionId,
            },
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiOrdersRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/refresh',
        });
    }

    /**
     * @param token
     * @returns any OK
     * @throws ApiError
     */
    public static resolveManualPaymentToken(
        token?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/payment/manual/resolve',
            query: {
                'token': token,
            },
        });
    }

    /**
     * @param uuid
     * @returns Order OK
     * @throws ApiError
     */
    public static orderFulfil(
        uuid: UUID,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/orders/{uuid}/fulfil',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns Order OK
     * @throws ApiError
     */
    public static newOrderCheckout(
        uuid: UUID,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/orders/new/{uuid}/checkout',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param nonZero
     * @returns PageableCollectionOrderDiscount OK
     * @throws ApiError
     */
    public static orderDiscountSearch(
        nonZero?: boolean,
    ): CancelablePromise<PageableCollectionOrderDiscount> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/orderdiscounts',
            query: {
                'nonZero': nonZero,
            },
        });
    }

    /**
     * @param requestBody
     * @returns OrderDiscount OK
     * @throws ApiError
     */
    public static orderDiscountCreate(
        requestBody?: OrderDiscount,
    ): CancelablePromise<OrderDiscount> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/orderdiscounts',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns Order OK
     * @throws ApiError
     */
    public static newOrderCheckoutCancel(
        uuid: UUID,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/orders/new/{uuid}/checkout/cancel',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns Order OK
     * @throws ApiError
     */
    public static newOrderSubmit(
        uuid: UUID,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/new/{uuid}/action/submit',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiOrdersMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/orders/metrics',
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns Order OK
     * @throws ApiError
     */
    public static newOrderCapturePaypalPayment(
        uuid: UUID,
        requestBody?: string,
    ): CancelablePromise<Order> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/new/{uuid}/action/payment/paypal/capture',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param requestUuid
     * @param paypalOrderId
     * @param token
     * @returns any OK
     * @throws ApiError
     */
    public static captureManualPaymentPaypalOrder(
        requestUuid: string,
        paypalOrderId?: string,
        token?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/orders/payment/manual/{requestUuid}/paypal/capture',
            path: {
                'requestUuid': requestUuid,
            },
            query: {
                'paypalOrderId': paypalOrderId,
                'token': token,
            },
        });
    }

    /**
     * @returns PageableCollectionCurrency OK
     * @throws ApiError
     */
    public static currenciesGet(): CancelablePromise<PageableCollectionCurrency> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/currencies',
        });
    }

    /**
     * @param requestBody
     * @returns PageableCollectionCurrency OK
     * @throws ApiError
     */
    public static currenciesPut(
        requestBody?: Array<Currency>,
    ): CancelablePromise<PageableCollectionCurrency> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/reference/currencies',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmailTemplate OK
     * @throws ApiError
     */
    public static getAllEmailTemplates(): CancelablePromise<Array<EmailTemplate>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/emailtemplates/all',
        });
    }

    /**
     * @returns ReferenceData OK
     * @throws ApiError
     */
    public static organisationType(): CancelablePromise<Array<ReferenceData>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/organisationtype',
        });
    }

    /**
     * @returns CountryInfo OK
     * @throws ApiError
     */
    public static countryInfoSearch(): CancelablePromise<Array<CountryInfo>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/countryinfo/all',
        });
    }

    /**
     * @returns CountryInfo OK
     * @throws ApiError
     */
    public static countryInfoSearchActive(): CancelablePromise<Array<CountryInfo>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/countryinfo/available',
        });
    }

    /**
     * @param path
     * @param metadata
     * @returns any OK
     * @throws ApiError
     */
    public static fileGet(
        path: string,
        metadata?: boolean,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/files/{path}',
            path: {
                'path': path,
            },
            query: {
                'metadata': metadata,
            },
        });
    }

    /**
     * @param path
     * @param _public
     * @param uuid
     * @returns ResourceFile OK
     * @throws ApiError
     */
    public static filePut(
        path: string,
        _public?: boolean,
        uuid?: UUID,
    ): CancelablePromise<ResourceFile> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/reference/files/{path}',
            path: {
                'path': path,
            },
            query: {
                'public': _public,
                'uuid': uuid,
            },
        });
    }

    /**
     * @param path
     * @returns void
     * @throws ApiError
     */
    public static fileDelete(
        path: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/reference/files/{path}',
            path: {
                'path': path,
            },
        });
    }

    /**
     * @param id
     * @returns EmailTemplate OK
     * @throws ApiError
     */
    public static getEmailTemplate(
        id: UUID,
    ): CancelablePromise<EmailTemplate> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/emailtemplates/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id
     * @param requestBody
     * @returns EmailTemplate OK
     * @throws ApiError
     */
    public static updateEmailTemplate(
        id: UUID,
        requestBody?: EmailTemplate,
    ): CancelablePromise<EmailTemplate> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/reference/emailtemplates/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id
     * @returns void
     * @throws ApiError
     */
    public static deleteEmailTemplate(
        id: UUID,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/reference/emailtemplates/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiReferenceMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/metrics',
        });
    }

    /**
     * @param requestBody
     * @returns EmailTemplate OK
     * @throws ApiError
     */
    public static createEmailTemplate(
        requestBody?: EmailTemplate,
    ): CancelablePromise<EmailTemplate> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/reference/emailtemplates',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiReferenceHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/reference/health',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiReferenceRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/reference/refresh',
        });
    }

    /**
     * @param requestBody
     * @returns Message OK
     * @throws ApiError
     */
    public static countryInfoPut(
        requestBody?: Array<CountryInfo>,
    ): CancelablePromise<Message> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/reference/countryinfo',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiAgreementsHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/agreements/health',
        });
    }

    /**
     * @param agreementUuid
     * @param uuid
     * @returns PowerPurchasePricing OK
     * @throws ApiError
     */
    public static agreementPricingGet(
        agreementUuid: UUID,
        uuid: UUID,
    ): CancelablePromise<PowerPurchasePricing> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/agreements/{agreementUuid}/pricing/{uuid}',
            path: {
                'agreementUuid': agreementUuid,
                'uuid': uuid,
            },
        });
    }

    /**
     * @param agreementUuid
     * @param uuid
     * @param requestBody
     * @returns PowerPurchasePricing OK
     * @throws ApiError
     */
    public static agreementPricingUpdate(
        agreementUuid: UUID,
        uuid: UUID,
        requestBody?: PowerPurchasePricing,
    ): CancelablePromise<PowerPurchasePricing> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/agreements/{agreementUuid}/pricing/{uuid}',
            path: {
                'agreementUuid': agreementUuid,
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param agreementUuid
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static agreementPricingDelete(
        agreementUuid: UUID,
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/agreements/{agreementUuid}/pricing/{uuid}',
            path: {
                'agreementUuid': agreementUuid,
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns PageableCollectionPowerPurchaseAgreement OK
     * @throws ApiError
     */
    public static agreementSearch(): CancelablePromise<PageableCollectionPowerPurchaseAgreement> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/agreements/',
        });
    }

    /**
     * @param requestBody
     * @returns PowerPurchaseAgreement OK
     * @throws ApiError
     */
    public static agreementCreate(
        requestBody?: PowerPurchaseAgreement,
    ): CancelablePromise<PowerPurchaseAgreement> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/agreements/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiAgreementsMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/agreements/metrics',
        });
    }

    /**
     * @param uuid
     * @returns PowerPurchaseAgreement OK
     * @throws ApiError
     */
    public static agreementGet(
        uuid: UUID,
    ): CancelablePromise<PowerPurchaseAgreement> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/agreements/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns PowerPurchaseAgreement OK
     * @throws ApiError
     */
    public static agreementUpdate(
        uuid: UUID,
        requestBody?: PowerPurchaseAgreement,
    ): CancelablePromise<PowerPurchaseAgreement> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/agreements/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static agreementDelete(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/agreements/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param agreementUuid
     * @returns PageableCollectionPowerPurchasePricing OK
     * @throws ApiError
     */
    public static agreementPricingSearch(
        agreementUuid: UUID,
    ): CancelablePromise<PageableCollectionPowerPurchasePricing> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/agreements/{agreementUuid}/pricing',
            path: {
                'agreementUuid': agreementUuid,
            },
        });
    }

    /**
     * @param agreementUuid
     * @param requestBody
     * @returns PowerPurchasePricing OK
     * @throws ApiError
     */
    public static agreementPricingCreate(
        agreementUuid: UUID,
        requestBody?: PowerPurchasePricing,
    ): CancelablePromise<PowerPurchasePricing> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/agreements/{agreementUuid}/pricing',
            path: {
                'agreementUuid': agreementUuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiAgreementsRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/agreements/refresh',
        });
    }

    /**
     * @param organisation
     * @param user
     * @returns PageableCollectionPaymentIntent OK
     * @throws ApiError
     */
    public static getStripePayments(
        organisation?: UUID,
        user?: UUID,
    ): CancelablePromise<PageableCollectionPaymentIntent> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/payments/stripe',
            query: {
                'organisation': organisation,
                'user': user,
            },
        });
    }

    /**
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static calculateDisbursements(
        requestBody?: Array<OwnerDisbursement>,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/financials/owner-disbursements/all',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PageableCollectionEnergyCompany OK
     * @throws ApiError
     */
    public static searchForEnergyCompanys(): CancelablePromise<PageableCollectionEnergyCompany> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/energycompanies',
        });
    }

    /**
     * @param requestBody
     * @returns PageableCollectionEnergyCompany OK
     * @throws ApiError
     */
    public static createEnergyCompanies(
        requestBody?: Array<EnergyCompany>,
    ): CancelablePromise<PageableCollectionEnergyCompany> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/financials/energycompanies',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param requestBody
     * @returns EnergyCompany OK
     * @throws ApiError
     */
    public static createEnergyCompany(
        requestBody?: EnergyCompany,
    ): CancelablePromise<EnergyCompany> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/financials/energycompanies',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static updateDisbursementStatus(
        uuid: string,
        requestBody?: any,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/financials/owner-disbursements/{uuid}/status',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static disbursementCalculator(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/financials/actions/disbursements',
        });
    }

    /**
     * @param organisationUuid
     * @param userUuid
     * @returns PageableCollectionAllocation OK
     * @throws ApiError
     */
    public static allocationsGetByOwner(
        organisationUuid?: UUID,
        userUuid?: UUID,
    ): CancelablePromise<PageableCollectionAllocation> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/owner-disbursements-admin/allocations',
            query: {
                'organisationUuid': organisationUuid,
                'userUuid': userUuid,
            },
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static postApiFinancialsRefresh(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/financials/refresh',
        });
    }

    /**
     * @returns any OK
     * @throws ApiError
     */
    public static getApiFinancialsHealth(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/health',
        });
    }

    /**
     * @returns any Created
     * @throws ApiError
     */
    public static calculateIncomes(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/financials/actions/incomes',
        });
    }

    /**
     * @param organisationUuid
     * @param userUuid
     * @returns PageableCollectionOwnerDisbursement OK
     * @throws ApiError
     */
    public static disbursementsGet(
        organisationUuid?: Array<string>,
        userUuid?: UUID,
    ): CancelablePromise<PageableCollectionOwnerDisbursement> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/owner-disbursements-admin',
            query: {
                'organisationUuid': organisationUuid,
                'userUuid': userUuid,
            },
        });
    }

    /**
     * @param requestBody
     * @returns OwnerDisbursement OK
     * @throws ApiError
     */
    public static disbursementCreate(
        requestBody?: OwnerDisbursement,
    ): CancelablePromise<OwnerDisbursement> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/financials/owner-disbursements-admin',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param formData
     * @returns any Parsed rows returned
     * @throws ApiError
     */
    public static disbursementUpload(
        formData: UploadExcelRequest,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/financials/disbursements/upload',
            formData: formData,
            mediaType: 'multipart/form-data',
        });
    }

    /**
     * @param organisation
     * @param personal
     * @returns PageableCollectionOwnerDisbursement OK
     * @throws ApiError
     */
    public static ownerDisbursementsGet(
        organisation?: Array<string>,
        personal?: boolean,
    ): CancelablePromise<PageableCollectionOwnerDisbursement> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/owner-disbursements',
            query: {
                'organisation': organisation,
                'personal': personal,
            },
        });
    }

    /**
     * @param uuid
     * @returns EnergyCompany OK
     * @throws ApiError
     */
    public static getEnergyCompany(
        uuid: UUID,
    ): CancelablePromise<EnergyCompany> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/energycompanies/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns EnergyCompany OK
     * @throws ApiError
     */
    public static updateEnergyCompany(
        uuid: UUID,
        requestBody?: EnergyCompany,
    ): CancelablePromise<EnergyCompany> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/financials/energycompanies/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns any OK
     * @throws ApiError
     */
    public static deleteEnergyCompany(
        uuid: UUID,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/financials/energycompanies/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @returns OwnerDisbursement OK
     * @throws ApiError
     */
    public static disbursementGet(
        uuid: UUID,
    ): CancelablePromise<OwnerDisbursement> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/owner-disbursements-admin/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @param uuid
     * @param requestBody
     * @returns OwnerDisbursement OK
     * @throws ApiError
     */
    public static disbursementUpdate(
        uuid: UUID,
        requestBody?: OwnerDisbursement,
    ): CancelablePromise<OwnerDisbursement> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/financials/owner-disbursements-admin/{uuid}',
            path: {
                'uuid': uuid,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param uuid
     * @returns void
     * @throws ApiError
     */
    public static disbursementDelete(
        uuid: UUID,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/financials/owner-disbursements-admin/{uuid}',
            path: {
                'uuid': uuid,
            },
        });
    }

    /**
     * @returns string OK
     * @throws ApiError
     */
    public static getApiFinancialsMetrics(): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/metrics',
        });
    }

    /**
     * @param organisation
     * @param user
     * @returns PageableCollectionOrderPayment OK
     * @throws ApiError
     */
    public static getPayments(
        organisation?: UUID,
        user?: UUID,
    ): CancelablePromise<PageableCollectionOrderPayment> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/financials/payments',
            query: {
                'organisation': organisation,
                'user': user,
            },
        });
    }

}
